import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  X,
  Loader2,
  Package,
  Search,
  Receipt,
  TrendingUp,
  AlertTriangle,
  Boxes,
  UserPlus,
  CreditCard,
  Banknote,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { products, members } from '../api/endpoints.js'

const CATEGORIES = [
  ['supplement', 'Supplements'],
  ['shake', 'Shakes'],
  ['apparel', 'Apparel'],
  ['equipment', 'Equipment'],
  ['drop_in', 'Drop-ins'],
  ['other', 'Other'],
]
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES)

const METHODS = [
  ['cash', 'Cash', Banknote],
  ['card', 'Card', CreditCard],
  ['credit', 'Account credit', Receipt],
]

const money = (n) => `$${Number(n || 0).toFixed(2)}`

const EMPTY_PRODUCT = {
  name: '',
  description: '',
  category: 'supplement',
  price: '',
  tax_rate: '0.06',
  sku: '',
  barcode: '',
  inventory: '',
  track_inventory: false,
  is_active: true,
}

// ─────────────────────────────────────────────────────────────────────────────
// Product editor
// ─────────────────────────────────────────────────────────────────────────────

function ProductModal({ product = null, onClose }) {
  const qc = useQueryClient()
  const isEdit = !!product
  const [form, setForm] = useState(
    isEdit
      ? {
          ...EMPTY_PRODUCT,
          ...product,
          price: String(product.price ?? ''),
          tax_rate: String(product.tax_rate ?? '0.06'),
          inventory: product.inventory === null || product.inventory === undefined
            ? ''
            : String(product.inventory),
          sku: product.sku ?? '',
          barcode: product.barcode ?? '',
          description: product.description ?? '',
        }
      : EMPTY_PRODUCT,
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const change = (e) => {
    const { name, value, type, checked } = e.target
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }))
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!form.name || form.price === '') {
      setError('Name and price are required.')
      return
    }
    setSaving(true)
    setError('')
    const payload = {
      name: form.name,
      description: form.description || null,
      category: form.category,
      price: parseFloat(form.price),
      tax_rate: parseFloat(form.tax_rate || '0'),
      sku: form.sku || null,
      barcode: form.barcode || null,
      inventory: form.inventory === '' ? null : parseInt(form.inventory, 10),
      track_inventory: !!form.track_inventory,
      is_active: !!form.is_active,
    }
    try {
      if (isEdit) {
        await products.update(product.id, payload)
        toast.success('Product updated')
      } else {
        await products.create(payload)
        toast.success('Product added')
      }
      qc.invalidateQueries({ queryKey: ['products'] })
      onClose()
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save product.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-surface-2 border border-surface-3 rounded-2xl w-full max-w-lg elev-2 animate-fade-in overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-surface-3 sticky top-0 bg-surface-2 z-10">
          <h2 className="text-lg font-semibold text-text-primary">
            {isEdit ? 'Edit Product' : 'Add Product'}
          </h2>
          <button onClick={onClose} className="text-text-dim hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {error && (
            <p className="text-sm text-status-suspended bg-status-suspended/10 border border-status-suspended/30 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <div>
            <label className="label">Name *</label>
            <input name="name" className="input-field" value={form.name}
                   onChange={change} placeholder="e.g. Protein Shake" required />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea name="description" rows={2} className="input-field resize-none"
                      value={form.description} onChange={change} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Category</label>
              <select name="category" className="input-field" value={form.category} onChange={change}>
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Price ($) *</label>
              <input name="price" type="number" step="0.01" min="0" className="input-field"
                     value={form.price} onChange={change} required />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Tax rate</label>
              <input name="tax_rate" type="number" step="0.001" min="0" max="1"
                     className="input-field" value={form.tax_rate} onChange={change} />
            </div>
            <div>
              <label className="label">SKU</label>
              <input name="sku" className="input-field" value={form.sku} onChange={change} />
            </div>
            <div>
              <label className="label">Barcode</label>
              <input name="barcode" className="input-field" value={form.barcode} onChange={change} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <label className="label">On hand</label>
              <input name="inventory" type="number" min="0" className="input-field"
                     placeholder="blank = unlimited" value={form.inventory} onChange={change} />
            </div>
            <label className="flex items-center gap-2 text-sm text-text-muted pb-2">
              <input type="checkbox" name="track_inventory" checked={form.track_inventory}
                     onChange={change} className="accent-primary w-4 h-4" />
              Track inventory
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-text-muted">
            <input type="checkbox" name="is_active" checked={form.is_active}
                   onChange={change} className="accent-primary w-4 h-4" />
            Available for sale
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : isEdit ? 'Save' : 'Add product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Member picker for attaching a sale
// ─────────────────────────────────────────────────────────────────────────────

function MemberPicker({ value, onChange }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)

  const { data } = useQuery({
    queryKey: ['members', 'pos-search', q],
    queryFn: () => members.list({ q, per_page: 8 }).then((r) => r.data),
    enabled: open && q.length >= 2,
  })
  const rows = data?.items || []

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-surface-3 bg-surface-3/40 px-3 py-2">
        <span className="text-sm text-text-primary truncate">{value.name}</span>
        <button onClick={() => onChange(null)} className="text-text-dim hover:text-status-suspended">
          <X className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="relative">
        <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-dim" />
        <input
          className="input-field pl-9"
          placeholder="Attach to member (optional)"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        />
      </div>
      {open && q.length >= 2 && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-surface-3 bg-surface-2 elev-2 max-h-56 overflow-y-auto">
          {rows.length === 0 && (
            <p className="px-3 py-2 text-sm text-text-dim">No matches</p>
          )}
          {rows.map((m) => (
            <button
              key={m.id}
              onClick={() => {
                onChange({ id: m.id, name: `${m.first_name} ${m.last_name}`.trim() })
                setQ(''); setOpen(false)
              }}
              className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-surface-3"
            >
              {m.first_name} {m.last_name}
              <span className="block text-xs text-text-dim">{m.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Register
// ─────────────────────────────────────────────────────────────────────────────

function Register({ catalog, loading }) {
  const qc = useQueryClient()
  const [cart, setCart] = useState([])          // [{product, qty}]
  const [member, setMember] = useState(null)
  const [method, setMethod] = useState('cash')
  const [discount, setDiscount] = useState('')
  const [q, setQ] = useState('')
  const [cat, setCat] = useState('')
  const [receipt, setReceipt] = useState(null)

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (catalog || []).filter((p) => {
      if (cat && p.category !== cat) return false
      if (!needle) return true
      return (
        p.name?.toLowerCase().includes(needle) ||
        p.sku?.toLowerCase().includes(needle) ||
        p.barcode?.toLowerCase().includes(needle)
      )
    })
  }, [catalog, q, cat])

  const add = (product) => {
    setCart((c) => {
      const hit = c.find((l) => l.product.id === product.id)
      if (hit) {
        return c.map((l) =>
          l.product.id === product.id ? { ...l, qty: l.qty + 1 } : l,
        )
      }
      return [...c, { product, qty: 1 }]
    })
  }

  const bump = (id, delta) =>
    setCart((c) =>
      c
        .map((l) => (l.product.id === id ? { ...l, qty: l.qty + delta } : l))
        .filter((l) => l.qty > 0),
    )

  const subtotal = cart.reduce((s, l) => s + l.product.price * l.qty, 0)
  const tax = cart.reduce((s, l) => s + l.product.price * l.qty * (l.product.tax_rate || 0), 0)
  const disc = Math.min(parseFloat(discount || '0') || 0, subtotal)
  const total = subtotal - disc + tax

  const sell = useMutation({
    mutationFn: () =>
      products.sell({
        lines: cart.map((l) => ({ product_id: l.product.id, quantity: l.qty })),
        member_id: member?.id || null,
        method,
        discount: disc,
      }).then((r) => r.data),
    onSuccess: (data) => {
      setReceipt(data)
      setCart([]); setMember(null); setDiscount('')
      qc.invalidateQueries({ queryKey: ['products'] })
      qc.invalidateQueries({ queryKey: ['product-sales'] })
      toast.success(`Sale complete — ${money(data.total)}`)
    },
    onError: (err) =>
      toast.error(err.response?.data?.detail || 'Sale failed'),
  })

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
      {/* Catalog */}
      <div>
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-dim" />
            <input className="input-field pl-9" placeholder="Search or scan barcode…"
                   value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select className="input-field w-auto" value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="skeleton h-24 rounded-xl" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="card text-center py-12">
            <Package className="w-8 h-8 mx-auto text-text-dim mb-3" />
            <p className="text-text-muted">No products yet.</p>
            <p className="text-sm text-text-dim">Add them from the Catalog tab.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {visible.map((p) => {
              const out = p.track_inventory && (p.inventory ?? 0) <= 0
              return (
                <button
                  key={p.id}
                  disabled={out}
                  onClick={() => add(p)}
                  className={[
                    'card-interactive text-left p-3 flex flex-col gap-1 h-full',
                    out ? 'opacity-40 cursor-not-allowed' : '',
                  ].join(' ')}
                >
                  <span className="text-sm font-semibold text-text-primary leading-snug line-clamp-2">
                    {p.name}
                  </span>
                  <span className="text-xs text-text-dim">{CATEGORY_LABEL[p.category] || p.category}</span>
                  <span className="mt-auto pt-2 flex items-center justify-between">
                    <span className="text-base font-bold text-primary">{money(p.price)}</span>
                    {p.track_inventory && (
                      <span className={[
                        'text-[11px] font-medium',
                        (p.inventory ?? 0) <= 5 ? 'text-status-alert' : 'text-text-dim',
                      ].join(' ')}>
                        {p.inventory ?? 0} left
                      </span>
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Cart */}
      <aside className="card p-4 h-fit lg:sticky lg:top-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-text-primary mb-3">
          <ShoppingCart className="w-4 h-4 text-primary" />
          Cart
          {cart.length > 0 && (
            <span className="badge-info">{cart.reduce((s, l) => s + l.qty, 0)}</span>
          )}
        </h3>

        {cart.length === 0 ? (
          <p className="text-sm text-text-dim py-6 text-center">Tap a product to start a sale.</p>
        ) : (
          <div className="space-y-2 mb-4 max-h-72 overflow-y-auto pr-1">
            {cart.map((l) => (
              <div key={l.product.id} className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary truncate">{l.product.name}</p>
                  <p className="text-xs text-text-dim">{money(l.product.price)} each</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => bump(l.product.id, -1)} className="btn-ghost p-1 rounded">
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-6 text-center text-sm text-text-primary">{l.qty}</span>
                  <button onClick={() => bump(l.product.id, 1)} className="btn-ghost p-1 rounded">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <span className="w-16 text-right text-sm font-medium text-text-primary">
                  {money(l.product.price * l.qty)}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3 border-t border-surface-3 pt-3">
          <MemberPicker value={member} onChange={setMember} />

          <div className="flex gap-2">
            {METHODS.map(([v, l, Icon]) => (
              <button
                key={v}
                onClick={() => setMethod(v)}
                className={[
                  'flex-1 flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[11px] transition-all',
                  method === v
                    ? 'border-primary/40 bg-primary/12 text-primary'
                    : 'border-surface-3 text-text-muted hover:text-text-primary',
                ].join(' ')}
              >
                <Icon className="w-4 h-4" />
                {l}
              </button>
            ))}
          </div>

          <div>
            <label className="label">Discount ($)</label>
            <input type="number" min="0" step="0.01" className="input-field"
                   value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0.00" />
          </div>

          <dl className="text-sm space-y-1">
            <div className="flex justify-between text-text-muted">
              <dt>Subtotal</dt><dd>{money(subtotal)}</dd>
            </div>
            {disc > 0 && (
              <div className="flex justify-between text-status-alert">
                <dt>Discount</dt><dd>-{money(disc)}</dd>
              </div>
            )}
            <div className="flex justify-between text-text-muted">
              <dt>Tax</dt><dd>{money(tax)}</dd>
            </div>
            <div className="flex justify-between text-base font-bold text-text-primary pt-1 border-t border-surface-3">
              <dt>Total</dt><dd>{money(total)}</dd>
            </div>
          </dl>

          <div className="flex gap-2">
            <button onClick={() => { setCart([]); setDiscount('') }}
                    disabled={cart.length === 0} className="btn-secondary px-3">
              <Trash2 className="w-4 h-4" />
            </button>
            <button onClick={() => sell.mutate()} disabled={cart.length === 0 || sell.isPending}
                    className="btn-primary flex-1">
              {sell.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `Charge ${money(total)}`}
            </button>
          </div>
          {member === null && method === 'credit' && (
            <p className="text-xs text-status-alert">Account credit needs a member attached.</p>
          )}
        </div>

        {receipt && (
          <div className="mt-4 rounded-lg border border-status-active/30 bg-status-active/10 p-3">
            <p className="text-sm font-semibold text-status-active flex items-center gap-2">
              <Receipt className="w-4 h-4" /> Last sale {money(receipt.total)}
            </p>
            <ul className="mt-1 text-xs text-text-muted space-y-0.5">
              {receipt.lines.map((l) => (
                <li key={l.id}>{l.quantity} × {l.product_name} — {money(l.total)}</li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Catalog management
// ─────────────────────────────────────────────────────────────────────────────

function Catalog({ catalog, loading }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null)
  const [adding, setAdding] = useState(false)

  const retire = useMutation({
    mutationFn: (id) => products.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] })
      toast.success('Product retired')
    },
    onError: () => toast.error('Could not retire product'),
  })

  const restock = useMutation({
    mutationFn: ({ id, delta }) => products.adjustInventory(id, { delta }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
    onError: (err) => toast.error(err.response?.data?.detail || 'Stock update failed'),
  })

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setAdding(true)} className="btn-primary">
          <Plus className="w-4 h-4" /> Add product
        </button>
      </div>

      <div className="table-container">
        <table className="table-base">
          <thead className="table-head">
            <tr>
              <th>Product</th>
              <th>Category</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Sold</th>
              <th>Revenue</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-text-dim">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </td></tr>
            )}
            {!loading && (catalog || []).length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-text-dim">
                No products in the catalog yet.
              </td></tr>
            )}
            {(catalog || []).map((p) => (
              <tr key={p.id} className="table-row" onClick={() => setEditing(p)}>
                <td>
                  <span className="font-medium">{p.name}</span>
                  {p.sku && <span className="block text-xs text-text-dim">SKU {p.sku}</span>}
                </td>
                <td className="text-text-muted">{CATEGORY_LABEL[p.category] || p.category}</td>
                <td>{money(p.price)}</td>
                <td>
                  {p.track_inventory ? (
                    <span className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button className="btn-ghost p-1 rounded"
                              onClick={() => restock.mutate({ id: p.id, delta: -1 })}>
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className={(p.inventory ?? 0) <= 5 ? 'text-status-alert font-medium' : ''}>
                        {p.inventory ?? 0}
                      </span>
                      <button className="btn-ghost p-1 rounded"
                              onClick={() => restock.mutate({ id: p.id, delta: 1 })}>
                        <Plus className="w-3 h-3" />
                      </button>
                    </span>
                  ) : (
                    <span className="text-text-dim">—</span>
                  )}
                </td>
                <td className="text-text-muted">{p.units_sold}</td>
                <td className="text-text-muted">{money(p.revenue)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn-ghost p-1.5 rounded text-text-dim hover:text-status-suspended"
                    onClick={() => {
                      if (confirm(`Retire ${p.name}? Past sales are kept.`)) retire.mutate(p.id)
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adding && <ProductModal onClose={() => setAdding(false)} />}
      {editing && <ProductModal product={editing} onClose={() => setEditing(null)} />}
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Sales history + report
// ─────────────────────────────────────────────────────────────────────────────

function Sales() {
  const qc = useQueryClient()
  const [days, setDays] = useState(30)

  const { data: summary } = useQuery({
    queryKey: ['product-sales', 'summary', days],
    queryFn: () => products.salesSummary({ days }).then((r) => r.data),
  })
  const { data: sales, isLoading } = useQuery({
    queryKey: ['product-sales', 'list', days],
    queryFn: () => products.sales({ days, limit: 200 }).then((r) => r.data),
  })

  const voidSale = useMutation({
    mutationFn: (id) => products.voidSale(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-sales'] })
      qc.invalidateQueries({ queryKey: ['products'] })
      toast.success('Sale voided and restocked')
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Void failed'),
  })

  return (
    <>
      <div className="flex justify-end mb-4">
        <select className="input-field w-auto" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <p className="text-xs text-text-dim uppercase tracking-wide">Revenue</p>
          <p className="text-2xl font-bold text-text-primary">{money(summary?.gross_revenue)}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs text-text-dim uppercase tracking-wide">Items sold</p>
          <p className="text-2xl font-bold text-text-primary">{summary?.units ?? 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs text-text-dim uppercase tracking-wide">Line items</p>
          <p className="text-2xl font-bold text-text-primary">{summary?.transactions ?? 0}</p>
        </div>
      </div>

      {(summary?.by_product?.length ?? 0) > 0 && (
        <div className="card p-4 mb-6">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-text-primary mb-3">
            <TrendingUp className="w-4 h-4 text-primary" /> Top sellers
          </h3>
          <div className="space-y-2">
            {summary.by_product.slice(0, 8).map((r) => {
              const pct = summary.gross_revenue
                ? Math.round((r.revenue / summary.gross_revenue) * 100)
                : 0
              return (
                <div key={r.product_id}>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-primary">{r.name}</span>
                    <span className="text-text-muted">{r.units} · {money(r.revenue)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-surface-3 mt-1 overflow-hidden">
                    <div className="h-full rounded-full bg-primary transition-all duration-500"
                         style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="table-base">
          <thead className="table-head">
            <tr>
              <th>When</th><th>Product</th><th>Qty</th><th>Total</th>
              <th>Member</th><th>Sold by</th><th />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-text-dim" />
              </td></tr>
            )}
            {!isLoading && (sales || []).length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-text-dim">
                No sales in this window.
              </td></tr>
            )}
            {(sales || []).map((s) => (
              <tr key={s.id} className="table-row cursor-default">
                <td className="text-text-muted whitespace-nowrap">
                  {s.sold_at ? new Date(s.sold_at).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                  }) : '—'}
                </td>
                <td className="font-medium">{s.product_name}</td>
                <td>{s.quantity}</td>
                <td>{money(s.total)}</td>
                <td className="text-text-muted">{s.member_name || 'Walk-in'}</td>
                <td className="text-text-muted">{s.staff_name || '—'}</td>
                <td>
                  <button
                    className="btn-ghost p-1.5 rounded text-text-dim hover:text-status-suspended"
                    onClick={() => { if (confirm('Void this sale and restock?')) voidSale.mutate(s.id) }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  ['register', 'Register', ShoppingCart],
  ['catalog', 'Catalog', Boxes],
  ['sales', 'Sales', Receipt],
]

export default function POSPage() {
  const [tab, setTab] = useState('register')

  const { data: catalog, isLoading } = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => products.list({ active_only: false }).then((r) => r.data),
  })

  const active = (catalog || []).filter((p) => p.is_active)
  const lowStock = (catalog || []).filter(
    (p) => p.track_inventory && (p.inventory ?? 0) <= 5,
  )

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Point of Sale</h1>
          <p className="text-sm text-text-muted mt-1">
            Shakes, supplements, apparel and drop-ins — rung up at the front desk.
          </p>
        </div>
        <div className="flex gap-1 p-1 rounded-xl bg-surface-2 border border-surface-3">
          {TABS.map(([v, l, Icon]) => (
            <button
              key={v}
              onClick={() => setTab(v)}
              className={['tab-btn flex items-center gap-2',
                tab === v ? 'tab-btn-active' : 'tab-btn-inactive'].join(' ')}
            >
              <Icon className="w-4 h-4" />
              {l}
            </button>
          ))}
        </div>
      </div>

      {lowStock.length > 0 && tab !== 'sales' && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-status-alert/30 bg-status-alert/10 px-3 py-2">
          <AlertTriangle className="w-4 h-4 text-status-alert mt-0.5 shrink-0" />
          <p className="text-sm text-status-alert">
            Low stock: {lowStock.map((p) => `${p.name} (${p.inventory ?? 0})`).join(', ')}
          </p>
        </div>
      )}

      {tab === 'register' && <Register catalog={active} loading={isLoading} />}
      {tab === 'catalog' && <Catalog catalog={catalog} loading={isLoading} />}
      {tab === 'sales' && <Sales />}
    </div>
  )
}
